/**
 * @fileoverview Audit event detail dialog.
 * @description Read-only dialog for one audit event (GET /audit/events/{auditEventId}): what
 * happened and with what result, who did it and to whom, what changed (before and after), the
 * session, device, network and approximate location it came from, the failure reason and any
 * allow-listed metadata. The backend masks names, emails and IPs unless the caller holds
 * AUDIT.SENSITIVE_DATA.READ with fresh MFA, so the dialog says which it is showing. Where an
 * approximate location is shown, the DB-IP attribution link the data licence requires is shown
 * with it.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import { useAuditEvent } from '@/features/audit/hooks/useAuditEvent'
import {
  ACTOR_TYPE_LABEL_KEY,
  describeResource,
  formatChangeValue,
  formatOccurredAt,
  humanizeCode,
  RESULT_LABEL_KEY,
  RESULT_VARIANT,
} from '@/features/audit/utils/audit-display.utils'
import Badge from '@/shared/components/atoms/Badge'
import ModalFooterButton from '@/shared/components/atoms/ModalFooterButton'
import DetailField from '@/shared/components/molecules/DetailField'
import DetailSectionHeading from '@/shared/components/molecules/DetailSectionHeading'
import Modal from '@/shared/components/molecules/Modal'

import type {
  AuditEventDetail,
  AuditEventDevice,
  AuditEventGeo,
} from '@/features/audit/types/audit.types'

interface AuditEventDetailModalProps {
  /** The event to show, or '' to keep the dialog closed. */
  auditEventId: string
  onClose: () => void
}

const PLACEHOLDER = '—'
const DB_IP_URL = 'https://db-ip.com'

/**
 * @description Joins the parts that exist with a separator, or "—" when none do.
 * @param {(string | undefined | null)[]} parts - The candidate parts.
 * @param {string} [separator] - What goes between parts. Defaults to " · ".
 * @returns {string} The joined text.
 */
function joinParts(parts: (string | undefined | null)[], separator = ' · '): string {
  const present = parts.filter((part): part is string => Boolean(part))
  return present.length > 0 ? present.join(separator) : PLACEHOLDER
}

/**
 * @description Describes a device on one line, e.g. "DESKTOP · Edge 154 · Windows 11".
 * @param {AuditEventDevice | undefined} device - The device.
 * @returns {string} The line, or "—" when nothing is known.
 */
function describeDevice(device: AuditEventDevice | undefined): string {
  if (!device) return PLACEHOLDER
  const browser = [device.browserName, device.browserVersion].filter(Boolean).join(' ')
  const os = [device.osName, device.osVersion].filter(Boolean).join(' ')
  return joinParts([device.deviceType, browser, os])
}

/**
 * @description Describes an approximate location on one line, e.g. "Pune, Maharashtra, IN".
 * @param {AuditEventGeo | undefined} geo - The location.
 * @returns {string} The line, or "—" when nothing is known.
 */
function describeGeo(geo: AuditEventGeo | undefined): string {
  if (!geo) return PLACEHOLDER
  return joinParts([geo.city, geo.region, geo.country], ', ')
}

/**
 * @component
 * @param {{ event: AuditEventDetail; locale: string }} props - The event and the active language.
 * @param {AuditEventDetail} props.event - The loaded event.
 * @param {string} props.locale - The active language tag, for the timestamp.
 */
function EventBody({ event, locale }: { event: AuditEventDetail; locale: string }) {
  const { t } = useTranslation()
  const hasContext = Boolean(
    event.session || event.device || event.network || event.geo || event.correlation,
  )

  return (
    <div className="flex flex-col gap-5">
      <p className="text-[12.5px] text-slate" data-testid="audit-detail-sensitive-notice">
        {event.sensitiveDataRevealed ? t('audit.detail.revealed') : t('audit.detail.masked')}
      </p>

      <section className="flex flex-col gap-2.5" data-testid="audit-detail-section-event">
        <DetailSectionHeading>{t('audit.detail.sections.event')}</DetailSectionHeading>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <DetailField
            label={t('audit.detail.fields.occurredAt')}
            data-testid="audit-detail-occurred-at"
          >
            {formatOccurredAt(event.occurredAt, locale)}
          </DetailField>
          <DetailField label={t('audit.detail.fields.result')} data-testid="audit-detail-result">
            <Badge
              variant={RESULT_VARIANT[event.result]}
              dot
              data-testid="audit-detail-result-badge"
            >
              {t(RESULT_LABEL_KEY[event.result])}
            </Badge>
          </DetailField>
          <DetailField
            label={t('audit.detail.fields.eventType')}
            data-testid="audit-detail-event-type"
          >
            <span className="font-mono text-[12.5px]">{event.eventType}</span>
          </DetailField>
          <DetailField
            label={t('audit.detail.fields.category')}
            data-testid="audit-detail-category"
          >
            <span className="font-mono text-[12.5px]">{event.eventCategory}</span>
          </DetailField>
          <DetailField label={t('audit.detail.fields.module')} data-testid="audit-detail-module">
            <span className="font-mono text-[12.5px]">{event.sourceModule}</span>
          </DetailField>
          <DetailField label={t('audit.detail.fields.eventId')} data-testid="audit-detail-event-id">
            <span className="break-all font-mono text-[12.5px]">{event.auditEventId}</span>
          </DetailField>
        </div>
      </section>

      <section className="flex flex-col gap-2.5" data-testid="audit-detail-section-actor">
        <DetailSectionHeading>{t('audit.detail.sections.actor')}</DetailSectionHeading>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <DetailField
            label={t('audit.detail.fields.actorType')}
            data-testid="audit-detail-actor-type"
          >
            {t(ACTOR_TYPE_LABEL_KEY[event.actor.actorType])}
          </DetailField>
          <DetailField
            label={t('audit.detail.fields.actorName')}
            data-testid="audit-detail-actor-name"
          >
            {event.actor.displayName ?? PLACEHOLDER}
          </DetailField>
          <DetailField label={t('audit.detail.fields.actorId')} data-testid="audit-detail-actor-id">
            <span className="break-all font-mono text-[12.5px]">
              {event.actor.actorId ?? PLACEHOLDER}
            </span>
          </DetailField>
          <DetailField label={t('audit.detail.fields.email')} data-testid="audit-detail-email">
            {event.actor.email ?? PLACEHOLDER}
          </DetailField>
        </div>
      </section>

      <section className="flex flex-col gap-2.5" data-testid="audit-detail-section-target">
        <DetailSectionHeading>{t('audit.detail.sections.target')}</DetailSectionHeading>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <DetailField
            label={t('audit.detail.fields.resource')}
            data-testid="audit-detail-resource"
          >
            <span className="break-all font-mono text-[12.5px]">
              {describeResource(event.resource)}
            </span>
          </DetailField>
          <DetailField
            label={t('audit.detail.fields.affectedUser')}
            data-testid="audit-detail-affected-user"
          >
            <span className="break-all font-mono text-[12.5px]">
              {event.affectedUser?.userId ?? PLACEHOLDER}
            </span>
          </DetailField>
          <DetailField label={t('audit.detail.fields.tenant')} data-testid="audit-detail-tenant">
            {event.tenant
              ? joinParts([event.tenant.tenantName, event.tenant.tenantId])
              : t('audit.detail.platformLevel')}
          </DetailField>
          {(event.region || event.branch) && (
            <DetailField
              label={t('audit.detail.fields.regionBranch')}
              data-testid="audit-detail-region-branch"
            >
              {joinParts([
                event.region?.regionName ?? event.region?.regionId,
                event.branch?.branchName ?? event.branch?.branchId,
              ])}
            </DetailField>
          )}
        </div>
      </section>

      {event.changes && event.changes.length > 0 && (
        <section className="flex flex-col gap-2.5" data-testid="audit-detail-section-changes">
          <DetailSectionHeading>{t('audit.detail.sections.changes')}</DetailSectionHeading>
          <div
            className="overflow-hidden rounded-lg border border-divider"
            data-testid="audit-detail-changes-table"
          >
            <div className="grid grid-cols-[1fr_1fr_1fr] gap-3 bg-input-bg px-3.5 py-2 text-[11px] font-semibold tracking-[0.6px] text-table-header">
              <span>{t('audit.detail.fields.field')}</span>
              <span>{t('audit.detail.fields.before')}</span>
              <span>{t('audit.detail.fields.after')}</span>
            </div>
            {event.changes.map((change) => (
              <div
                key={change.field}
                data-testid="audit-detail-change-row"
                className="grid grid-cols-[1fr_1fr_1fr] gap-3 border-t border-row-border px-3.5 py-2.5 text-[13px] text-ink"
              >
                <span
                  className="break-all font-mono text-[12.5px]"
                  data-testid="audit-detail-change-field"
                >
                  {change.field}
                </span>
                <span className="break-all" data-testid="audit-detail-change-before">
                  {formatChangeValue(change.before)}
                </span>
                <span className="break-all" data-testid="audit-detail-change-after">
                  {formatChangeValue(change.after)}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {event.failure && (
        <section className="flex flex-col gap-2.5" data-testid="audit-detail-section-failure">
          <DetailSectionHeading>{t('audit.detail.sections.failure')}</DetailSectionHeading>
          <DetailField label={t('audit.detail.fields.reason')} data-testid="audit-detail-reason">
            <span className="font-mono text-[12.5px]">{event.failure.reasonCode}</span>
          </DetailField>
        </section>
      )}

      {hasContext && (
        <section className="flex flex-col gap-2.5" data-testid="audit-detail-section-context">
          <DetailSectionHeading>{t('audit.detail.sections.context')}</DetailSectionHeading>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <DetailField
              label={t('audit.detail.fields.sessionId')}
              data-testid="audit-detail-session-id"
            >
              <span className="break-all font-mono text-[12.5px]">
                {event.session?.sessionId ?? PLACEHOLDER}
              </span>
            </DetailField>
            <DetailField
              label={t('audit.detail.fields.correlationId')}
              data-testid="audit-detail-correlation-id"
            >
              <span className="break-all font-mono text-[12.5px]">
                {event.correlation?.correlationId ?? PLACEHOLDER}
              </span>
            </DetailField>
            <DetailField
              label={t('audit.detail.fields.requestId')}
              data-testid="audit-detail-request-id"
            >
              <span className="break-all font-mono text-[12.5px]">
                {event.correlation?.requestId ?? PLACEHOLDER}
              </span>
            </DetailField>
            <DetailField
              label={t('audit.detail.fields.traceId')}
              data-testid="audit-detail-trace-id"
            >
              <span className="break-all font-mono text-[12.5px]">
                {event.correlation?.traceId ?? PLACEHOLDER}
              </span>
            </DetailField>
            <DetailField label={t('audit.detail.fields.device')} data-testid="audit-detail-device">
              {describeDevice(event.device)}
            </DetailField>
            <DetailField
              label={t('audit.detail.fields.ipAddress')}
              data-testid="audit-detail-ip-address"
            >
              <span className="font-mono text-[12.5px]">
                {event.network?.ipAddress ?? PLACEHOLDER}
              </span>
            </DetailField>
            <DetailField
              label={t('audit.detail.fields.userAgent')}
              data-testid="audit-detail-user-agent"
            >
              <span className="break-all">{event.network?.userAgent ?? PLACEHOLDER}</span>
            </DetailField>
            <DetailField
              label={t('audit.detail.fields.location')}
              data-testid="audit-detail-location"
            >
              <span>{describeGeo(event.geo)}</span>
              {event.geo && (
                <span className="mt-0.5 block text-[11.5px] text-slate">
                  {t('audit.detail.geoAttribution')}{' '}
                  <a
                    href={DB_IP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="audit-detail-geo-attribution-link"
                    className="font-semibold text-primary underline"
                  >
                    DB-IP
                  </a>
                </span>
              )}
            </DetailField>
          </div>
        </section>
      )}

      {event.metadata && Object.keys(event.metadata).length > 0 && (
        <section className="flex flex-col gap-2.5" data-testid="audit-detail-section-metadata">
          <DetailSectionHeading>{t('audit.detail.sections.metadata')}</DetailSectionHeading>
          <pre
            className="overflow-x-auto rounded-lg bg-input-bg p-3 font-mono text-xs text-ink"
            data-testid="audit-detail-metadata"
          >
            {JSON.stringify(event.metadata, null, 2)}
          </pre>
        </section>
      )}
    </div>
  )
}

/**
 * @component
 * @param {AuditEventDetailModalProps} props - Which event to show and how to close the dialog.
 * @param {string} props.auditEventId - The event to show, or '' to keep the dialog closed.
 * @param {() => void} props.onClose - Called on Escape, a backdrop click, or either close button.
 */
function AuditEventDetailModal({ auditEventId, onClose }: AuditEventDetailModalProps) {
  const { t, i18n } = useTranslation()
  const detailQuery = useAuditEvent(auditEventId)
  const event = detailQuery.data

  return (
    <Modal
      open={auditEventId !== ''}
      onClose={onClose}
      size="lg"
      data-testid="audit-detail-modal"
      title={t('audit.detail.title')}
      subtitle={event ? humanizeCode(event.eventType) : ''}
      footer={
        <ModalFooterButton type="button" onClick={onClose} data-testid="audit-detail-close-button">
          {t('audit.detail.close')}
        </ModalFooterButton>
      }
    >
      {detailQuery.isPending && detailQuery.fetchStatus !== 'idle' && (
        <p role="status" className="text-[13px] text-slate" data-testid="audit-detail-loading">
          {t('audit.detail.loading')}
        </p>
      )}

      {detailQuery.isError && (
        <p
          role="alert"
          className="text-[13px] text-danger"
          data-testid="audit-detail-error-message"
        >
          {detailQuery.error.message || t('audit.detail.loadError')}
        </p>
      )}

      {event && <EventBody event={event} locale={i18n.resolvedLanguage ?? 'en'} />}
    </Modal>
  )
}

export default AuditEventDetailModal
